import { Assets, Sprite, Texture } from 'pixi.js';

import lottie, { type AnimationItem } from 'lottie-web';

type LottieOptions = {
  /** Lottie动画文件路径 */
  asset: string | Uint8Array;
  /** 是否自动播放 */
  autoplay?: boolean;
  /** 是否循环播放 */
  loop?: boolean;

  width?: number;

  height?: number;

  /** lottie 播放速度 */
  speed?: number;
};

/**
 * LottieSprite
 * @description Lottie动画精灵
 * @example
 * const lottieSprite = new LottieSprite({
 *  asset: "lottie.json",
 * autoplay: true,
 * loop: true,
 * width: 100,
 * height: 100,
 * speed: 1,
 * });
 * app.stage.addChild(lottieSprite);
 * lottieSprite.play();
 * lottieSprite.stop();
 * lottieSprite.destroy();
 */
export class LottieSprite extends Sprite {
  private _lottieAnimation?: AnimationItem;
  private _file?: File | Uint8Array;
  private _canvas?: OffscreenCanvas | HTMLCanvasElement;
  private _playing = false;
  private _destroyed = false;
  private _domLoadedHandler?: () => void;
  private _textureUpdateHandler?: () => void;
  private _completeHandler?: () => void;
  private _progressFrameHandler?: () => void;

  onCompleted?: () => void;
  onProgress?: (progress: number) => void;
  enterFrame?: (currentFrame: number) => void;

  constructor(options: LottieOptions) {
    const pixelRatio = Math.max(16 / 9, window.devicePixelRatio);
    const width = (options.width ?? 100) * pixelRatio;
    const height = (options.height ?? 100) * pixelRatio;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;

    super({ texture: Texture.from(tempCanvas) });

    this._canvas = tempCanvas;
    this._canvas.width = this.width * pixelRatio;
    this._canvas.height = this.height * pixelRatio;
    this.texture.update();

    this.initLottie(options).then(() => {
      if (this._destroyed || !this._lottieAnimation) return;

      this._domLoadedHandler = () => {
        if (!this._lottieAnimation || this._destroyed) return;

        this._playing = false;
        this._textureUpdateHandler = () => {
          if (this._destroyed || !this.texture?.source) return;
          this.texture.source.update();
        };

        this._lottieAnimation.addEventListener(
          'enterFrame',
          this._textureUpdateHandler
        );
      };

      this._lottieAnimation.addEventListener(
        'DOMLoaded',
        this._domLoadedHandler
      );
      this.initEvent();
    });
  }

  private async initLottie(options: LottieOptions) {
    this._file =
      typeof options.asset === 'string'
        ? await Assets.load(options.asset)
        : options.asset;

    if (this._destroyed) return;

    this.width = options.width ?? 100;
    this.height = options.height ?? 100;

    // 初始化Lottie动画
    this._lottieAnimation = lottie.loadAnimation<'canvas'>({
      renderer: 'canvas',
      loop: options.loop ?? false,
      autoplay: options.autoplay ?? false,
      animationData: this._file,
      rendererSettings: {
        context: this._canvas?.getContext('2d'),
        clearCanvas: true,
      },
    } as any);
    if (options.speed) {
      this._lottieAnimation.setSpeed(options.speed || 1);
    }
  }

  private initEvent() {
    // 当动画播放完成时触发
    this._completeHandler = () => {
      if (this._destroyed) return;
      this.onCompleted?.();
    };
    this._lottieAnimation?.addEventListener('complete', this._completeHandler);

    // onProgress
    this._progressFrameHandler = () => {
      if (this._lottieAnimation && !this._destroyed) {
        this.enterFrame?.(this._lottieAnimation.currentFrame);

        this.onProgress?.(
          (this._lottieAnimation.currentFrame /
            this._lottieAnimation.totalFrames) *
            100 || 0
        );
      }
    };
    this._lottieAnimation?.addEventListener(
      'enterFrame',
      this._progressFrameHandler
    );
  }

  get currentFrame() {
    return this._lottieAnimation?.currentFrame || 0;
  }

  play() {
    if (!this._destroyed && !this._playing) {
      this._playing = true;
      this._lottieAnimation?.goToAndPlay(0, true);
    }
  }

  stop() {
    if (this._playing) {
      this._playing = false;
      this._lottieAnimation?.pause();
    }
  }

  private removeLottieEventListeners() {
    if (!this._lottieAnimation) return;

    if (this._domLoadedHandler) {
      this._lottieAnimation.removeEventListener(
        'DOMLoaded',
        this._domLoadedHandler
      );
    }
    if (this._textureUpdateHandler) {
      this._lottieAnimation.removeEventListener(
        'enterFrame',
        this._textureUpdateHandler
      );
    }
    if (this._completeHandler) {
      this._lottieAnimation.removeEventListener(
        'complete',
        this._completeHandler
      );
    }
    if (this._progressFrameHandler) {
      this._lottieAnimation.removeEventListener(
        'enterFrame',
        this._progressFrameHandler
      );
    }
  }

  destroy(options?: Parameters<Sprite['destroy']>[0]) {
    if (this._destroyed) return;

    this._destroyed = true;
    this.stop();
    this.removeLottieEventListeners();
    this._lottieAnimation?.destroy();
    this._lottieAnimation = undefined;
    this._canvas = undefined;
    super.destroy(options);
  }
}
